import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useMatch, useLocation, Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { getStellen, createStelle, deleteStelle, updateStelle, getProfile, getProfil, abrechnungAbrufen, feedbackSenden, checkoutSessionErstellen, starteAnalyse, getCVVorlagen, getAnschreibenVorlagen, rematchKeywords } from './api';
import { hatLebenslaufInhalt } from './utils/profilHelfer';
import { istEntwurf } from './utils/themeUtils';
import StellenListe from './components/StellenListe';
import StellenEditor from './components/StellenEditor';
import ToolPanel, { PanelActions } from './components/ToolPanel';
import ProfilModal from './components/ProfilModal';
import FirstProfileSetupModal from './components/FirstProfileSetupModal';
import DashboardTutorial from './components/DashboardTutorial';
import AnalyticsTutorial from './components/AnalyticsTutorial';
import StellenEditorTutorial from './components/StellenEditorTutorial';
import CvImportOverlay from './components/CvImportOverlay';
import Dashboard from './Dashboard';
import Papierkorb from './components/Papierkorb';
import Analytics from './Analytics';
import SucheView from './components/SucheView';
import ProfilEinstellungen from './components/ProfilEinstellungen';
import AdminPanel from './components/AdminPanel';
import MobileStudio from './MobileStudio';
import MeineDokumente from './MeineDokumente';
import PricingModal from './components/PricingModal';
import DotField from './onboarding/DotField';
import { useRolle } from './hooks/useRolle';
import { useJobs } from './contexts/JobsContext';
import ElasticResizeHandle from './components/ElasticResizeHandle';
import { motion, useMotionValue, useTransform, animate } from 'motion/react';
import { AnimateIcon } from './components/animate-ui/icons/AnimateIcon';
import { LayoutDashboard } from './components/animate-ui/icons/LayoutDashboard';
import { Search as SearchAnimated } from './components/animate-ui/icons/Search';
import { ChartColumn } from './components/animate-ui/icons/ChartColumn';
import { ClipboardCheck } from './components/animate-ui/icons/ClipboardCheck';
import { capture } from './tracking/posthog';
import './Studio.css';

// eslint-disable-next-line no-unused-vars
const SearchIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 22L20 20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const AnalyticsIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_4418_5197)">
      <path opacity="0.4" d="M21 22.75H3C2.59 22.75 2.25 22.41 2.25 22C2.25 21.59 2.59 21.25 3 21.25H21C21.41 21.25 21.75 21.59 21.75 22C21.75 22.41 21.41 22.75 21 22.75Z" fill={color}/>
      <path d="M5.59998 8.38086H4C3.45 8.38086 3 8.83086 3 9.38086V18.0009C3 18.5509 3.45 19.0009 4 19.0009H5.59998C6.14998 19.0009 6.59998 18.5509 6.59998 18.0009V9.38086C6.59998 8.82086 6.14998 8.38086 5.59998 8.38086Z" fill={color}/>
      <path d="M12.8002 5.18945H11.2002C10.6502 5.18945 10.2002 5.63945 10.2002 6.18945V17.9995C10.2002 18.5495 10.6502 18.9995 11.2002 18.9995H12.8002C13.3502 18.9995 13.8002 18.5495 13.8002 17.9995V6.18945C13.8002 5.63945 13.3502 5.18945 12.8002 5.18945Z" fill={color}/>
      <path d="M19.9999 2H18.3999C17.8499 2 17.3999 2.45 17.3999 3V18C17.3999 18.55 17.8499 19 18.3999 19H19.9999C20.5499 19 20.9999 18.55 20.9999 18V3C20.9999 2.45 20.5499 2 19.9999 2Z" fill={color}/>
    </g>
    <defs>
      <clipPath id="clip0_4418_5197">
        <rect width="24" height="24" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const TemplatesIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 10V15C22 20 20 22 15 22H9C4 22 2 20 2 15V9C2 4 4 2 9 2H14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 10H18C15 10 14 9 14 6V2L22 10Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path opacity="0.4" fillRule="evenodd" clipRule="evenodd" d="M8.31975 12H10.9698C11.2398 12 11.5798 12.18 11.7198 12.4L12.8498 14.1C13.0798 14.44 13.0298 14.95 12.7398 15.24L10.2798 17.7C9.92975 18.05 9.34975 18.05 8.99975 17.7L6.53975 15.24C6.24975 14.95 6.19975 14.44 6.42975 14.1L7.55975 12.4C7.71975 12.18 8.05975 12 8.31975 12Z" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const DashboardIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_4418_10077)">
      <path d="M9.02 2.84016L3.63 7.04016C2.73 7.74016 2 9.23016 2 10.3602V17.7702C2 20.0902 3.89 21.9902 6.21 21.9902H17.79C20.11 21.9902 22 20.0902 22 17.7802V10.5002C22 9.29016 21.19 7.74016 20.2 7.05016L14.02 2.72016C12.62 1.74016 10.37 1.79016 9.02 2.84016Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10.5 18H13.5C15.15 18 16.5 16.65 16.5 15V12C16.5 10.35 15.15 9 13.5 9H10.5C8.85 9 7.5 10.35 7.5 12V15C7.5 16.65 8.85 18 10.5 18Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 9V18" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7.5 13.5H16.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs>
      <clipPath id="clip0_4418_10077">
        <rect width="24" height="24" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const IdeaIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_4418_9918)">
      <path d="M8.29999 18.0402V16.8802C5.99999 15.4902 4.10999 12.7802 4.10999 9.90018C4.10999 4.95018 8.65999 1.07018 13.8 2.19018C16.06 2.69018 18.04 4.19018 19.07 6.26018C21.16 10.4602 18.96 14.9202 15.73 16.8702V18.0302C15.73 18.3202 15.84 18.9902 14.77 18.9902H9.25999C8.15999 19.0002 8.29999 18.5702 8.29999 18.0402Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.5 21.9992C10.79 21.3492 13.21 21.3492 15.5 21.9992" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs><clipPath id="clip0_4418_9918"><rect width="24" height="24" fill="white"/></clipPath></defs>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const GlockeIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_655_7226)">
      <path d="M22 19.78C22 21 21 22 19.78 22H4.22C3 22 2 21 2 19.78C2 18.56 3 17.56 4.22 17.56H19.78C21 17.56 22 18.56 22 19.78ZM12 2C10.78 2 9.78 3 9.78 4.22C9.78 5.44 10.78 6.44 12 6.44C13.22 6.44 14.22 5.44 14.22 4.22C14.22 3 13.22 2 12 2Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.0001 6.44C8.33008 6.44 5.33008 9.44 5.33008 13.11V17.55H18.6601V13.11C18.6601 9.44 15.6601 6.44 11.9901 6.44H12.0001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs><clipPath id="clip0_655_7226"><rect width="24" height="24" fill="white"/></clipPath></defs>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const ProfilIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_profilicon)">
      <path d="M12.1601 10.87C12.0601 10.86 11.9401 10.86 11.8301 10.87C9.45006 10.79 7.56006 8.84 7.56006 6.44C7.56006 3.99 9.54006 2 12.0001 2C14.4501 2 16.4401 3.99 16.4401 6.44C16.4301 8.84 14.5401 10.79 12.1601 10.87Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.16021 14.56C4.74021 16.18 4.74021 18.82 7.16021 20.43C9.91021 22.27 14.4202 22.27 17.1702 20.43C19.5902 18.81 19.5902 16.17 17.1702 14.56C14.4302 12.73 9.92021 12.73 7.16021 14.56Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs><clipPath id="clip0_profilicon"><rect width="24" height="24" fill="white"/></clipPath></defs>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const EinstellungenIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_einstellicon)">
      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs><clipPath id="clip0_einstellicon"><rect width="24" height="24" fill="white"/></clipPath></defs>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const FIXED_OVERHEAD = 52; // 32px body padding + 20px resize handles
// eslint-disable-next-line no-unused-vars
const A4_MIN = 520;        // minimum middle-column width for readable A4 preview
const MOBILE_BP = 960;
// ponytail: regime-table — tight=960-1299, laptop=1300-1599, normal=≥1600, ultra=21:9
const REGIME_BOUNDS = {
  tight:  { sMin: 220, sMax: 260, pMin: 200, pMax: 240 },
  laptop: { sMin: 260, sMax: 300, pMin: 240, pMax: 320 },
  normal: { sMin: 300, sMax: 350, pMin: 250, pMax: 500 },
  ultra:  { sMin: 390, sMax: 455, pMin: 325, pMax: 650 },
};
function computeRegime(w, isUltra) {
  if (isUltra && w >= 1600) return 'ultra';
  if (w < 1300) return 'tight';
  if (w < 1600) return 'laptop';
  return 'normal';
}
const CENTER_MIN = 480;    // guaranteed center column minimum width
const SHELL_OVERHEAD = 48; // 24px padding + two 12px resize handles
const LEGACY_FIRST_PROFILE_SETUP_DONE_KEY = 'cvflow_first_profile_setup_done';
const ONBOARDING_PROFILE_SEED_KEY = 'cvflow_onboarding_profile_seed';

function firstProfileSetupDoneKey(nutzer) {
  return nutzer?.uid
    ? `cvflow_first_profile_setup_done:${nutzer.uid}`
    : LEGACY_FIRST_PROFILE_SETUP_DONE_KEY;
}

function forceProfileSetupKey(nutzer) {
  return nutzer?.uid
    ? `cvflow_force_profile_setup:${nutzer.uid}`
    : 'cvflow_force_profile_setup';
}

function KaufErfolgView({ onWeiter }) {
  useEffect(() => { capture('purchase_completed', {}); }, []);
  return (
    <div className="kauf-view">
      <div className="kauf-view-card kauf-view-card--erfolg">
        <div className="kauf-checkmark">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="24" fill="rgba(5,150,105,0.15)" />
            <path d="M14 24.5L21 31.5L34 17" stroke="#059669" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <span className="kauf-eyebrow">Zahlung erfolgreich</span>
        <h2 className="kauf-headline">Dein Abo ist aktiv.</h2>
        <p className="kauf-sub">
          Du hast jetzt unbegrenzten Zugriff auf den Agenten-Workflow.
        </p>
        <button className="kauf-btn-primary" onClick={onWeiter}>
          Zurück zum Dashboard →
        </button>
      </div>
    </div>
  );
}

function KaufAbbruchView({ onNochmal, onZurueck, kauftGrade }) {
  return (
    <div className="kauf-view">
      <div className="kauf-view-card">
        <div className="kauf-checkmark">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="24" fill="rgba(220,38,38,0.1)" />
            <path d="M17 17L31 31M31 17L17 31" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </div>
        <span className="kauf-eyebrow">Zahlung abgebrochen</span>
        <h2 className="kauf-headline">Kein Problem —<br />jederzeit nachholen.</h2>
        <p className="kauf-sub">
          Es wurde nichts berechnet. Du kannst den Kauf jederzeit über
          die Sidebar starten.
        </p>
        <div className="kauf-btn-row">
          <button className="kauf-btn-primary" onClick={onNochmal} disabled={kauftGrade}>
            {kauftGrade ? 'Weiterleitung…' : 'Nochmal versuchen →'}
          </button>
          <button className="kauf-btn-ghost" onClick={onZurueck}>
            Zurück zum Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

function Studio({ nutzer }) {
  const navigate = useNavigate();
  const location = useLocation();
  const stelleMatch = useMatch('/stelle/:id');

  const { rolle } = useRolle();
  const [kauftGrade, setKauftGrade] = useState(false);
  const kaufen = useCallback(async (preisId, ts, paketTyp) => {
    capture('checkout_started', { plan: paketTyp || preisId });
    setKauftGrade(true);
    try {
      const r = await checkoutSessionErstellen(preisId, ts);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      console.error('[Stripe] Checkout fehlgeschlagen', e);
      setKauftGrade(false);
    }
  }, []);
  const { jobs, onAnyJobDone, registerJob } = useJobs();
  // ponytail: tracks ids already patched this session to avoid redundant PATCHes
  const geflipptRef = useRef(new Set());

  // Derive view from URL — no useState needed
  const stelleIdParam = stelleMatch?.params?.id ?? null;
  const view = stelleIdParam ? 'editor'
    : location.pathname.startsWith('/admin') ? 'admin'
    : location.pathname === '/analytics' ? 'analytics'
    : location.pathname === '/suche' ? 'suche'
    : location.pathname === '/profileinstellungen' ? 'profileinstellungen'
    : location.pathname === '/meine-dokumente' ? 'dokumente'
    : location.pathname === '/kauf/erfolg' ? 'kauf_erfolg'
    : location.pathname === '/kauf/abbruch' ? 'kauf_abbruch'
    : 'dashboard';

  // Schmale Symbol-Spalte nur im Bearbeitungsmodus einer Bewerbung — eigener
  // Modus, unabhängig von der manuell ziehbaren Breite. Provisorisch per Knopf
  // im Editor an-/ausschaltbar, bis das echte Bearbeitungswerkzeug existiert.
  const [bearbeitungsmodus, setBearbeitungsmodus] = useState(false);
  const istRail = bearbeitungsmodus && view === 'editor';
  // Rail-Breite wird inline gesetzt (Framer schreibt sonst eine feste Breite ins
  // Element, die jedes CSS überschreibt).
  const sidebarRef = useRef(null);
  const RAIL_W = 82;
  // Kurzzeitig aktive Breiten-Animation beim Umschalten (auch beim Zurückwachsen,
  // wo die is-rail-Klasse — und damit ihr Übergang — sofort wegfällt).
  const [railAnim, setRailAnim] = useState(false);
  const railAnimTimer = useRef(null);

  // Verlässt man den Editor, Bearbeitungsmodus automatisch beenden.
  useEffect(() => {
    if (view !== 'editor') setBearbeitungsmodus(false);
  }, [view]);

  const [stellen, setStellen] = useState([]);
  const cvflowStellen = stellen
    .filter(s => (s.quelle || 'cvflow') !== 'extern')
    .filter(s => s.quelle !== 'lebenslauf')
    .filter(s => !(s.bewerbungs_status || '').startsWith('papierkorb'))
    .map(s => {
      // Aktiven Job (aus JobsContext, überlebt Navigation) live auf die Stelle mergen
      const job = Object.values(jobs).find(
        j => j.stelleId === s.id && j.status !== 'fertig' && j.status !== 'fehler'
      );
      if (!job || job.status === 'warten') {
        // ponytail: strip optimistic _running* fields baked in by onGenerierenDirekt
        const { _runningStatus, _runningSchritt, _runningIndex, _runningTotal, _runningJobId, ...rest } = s;
        return rest;
      }
      return {
        ...s,
        _runningStatus: job.status,
        _runningSchritt: job.schritt,
        _runningIndex: job.schritt_index,
        _runningTotal: job.schritt_total,
      };
    });
  // Leere Lebenslauf-Entwürfe sind keine Stellenanzeigen — eigene Liste für "Meine Dokumente".
  const lebenslaufEntwuerfe = stellen
    .filter(s => s.quelle === 'lebenslauf')
    .filter(s => !(s.bewerbungs_status || '').startsWith('papierkorb'));
  const [aktiveStelle, setAktiveStelle] = useState(null);
  const [tonlage, setTonlage] = useState('klassisch');
  const [profilModalOffen, setProfilModalOffen] = useState(false);
  const [profilModalDateiname, setProfilModalDateiname] = useState(null);
  const [profilModalInitialAnsicht, setProfilModalInitialAnsicht] = useState(null);
  const [profilModalInitialSektion, setProfilModalInitialSektion] = useState(null);
  const [profilModalInitialSektionWert, setProfilModalInitialSektionWert] = useState(null);
  const profilGeaendertRef = useRef(false); // wird true wenn Profil im Modal gespeichert wurde
  const [pricingModalOffen, setPricingModalOffen] = useState(false);

  const [keinProfilWarnung, setKeinProfilWarnung] = useState(false);
  const [hatProfile, setHatProfile] = useState(true); // optimistic default
  const [profilLaedt, setProfilLaedt] = useState(true);
  const [aktivesProfile, setAktivesProfile] = useState(undefined);
  const [firstSetupOffen, setFirstSetupOffen] = useState(false);
  const [lebenslaufUploadOffen, setLebenslaufUploadOffen] = useState(false);
  const [feedbackModalOffen, setFeedbackModalOffen] = useState(false);
  const [feedbackBetreff, setFeedbackBetreff] = useState('');
  const [feedbackNachricht, setFeedbackNachricht] = useState('');
  const [feedbackBilder, setFeedbackBilder] = useState([]);
  const [feedbackLaedt, setFeedbackLaedt] = useState(false);
  const [feedbackGesendet, setFeedbackGesendet] = useState(false);
  const [feedbackFehler, setFeedbackFehler] = useState('');
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem('dark_mode') === 'true'
  );

  const toggleDarkMode = () => {
    const next = !darkMode;
    const apply = () => {
      setDarkMode(next);
      localStorage.setItem('dark_mode', String(next));
      document.documentElement.setAttribute('data-theme', next ? 'dark' : '');
    };
    if (document.startViewTransition) {
      document.startViewTransition(apply);
    } else {
      apply();
    }
  };
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BP);
  const [regime, setRegime] = useState(() => computeRegime(window.innerWidth, window.matchMedia('(min-aspect-ratio: 21/9) and (min-width: 1600px)').matches));
  const [viewportW, setViewportW] = useState(() => window.innerWidth);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [panelWidth, setPanelWidth] = useState(250);
  const { sMin: activeSidebarMin, sMax: _sMaxBound, pMin: activePanelMin, pMax: _pMaxBound } = REGIME_BOUNDS[regime];
  // Dynamic max: neither sidebar may grow so large that center falls below CENTER_MIN
  const activeSidebarMax = Math.max(activeSidebarMin, Math.min(_sMaxBound, viewportW - panelWidth   - CENTER_MIN - SHELL_OVERHEAD));
  // Im Rail-Modus belegt die linke Leiste nur RAIL_W und der rechten Leiste
  // wird ein Bonus auf die Maximalbreite gewährt — beides gibt mehr Platz.
  const panelSidebarFootprint = istRail ? RAIL_W : sidebarWidth;
  // Im Bearbeitungsmodus darf die rechte Leiste bis 40% von „Mittelfeld + rechte
  // Leiste“ (Display ohne die linke Schmal-Leiste) aufgezogen werden.
  const panelMaxBound = istRail
    ? Math.round(0.40 * (viewportW - RAIL_W - SHELL_OVERHEAD))
    : _pMaxBound;
  const activePanelMax   = Math.max(activePanelMin,   Math.min(panelMaxBound, viewportW - panelSidebarFootprint - CENTER_MIN - SHELL_OVERHEAD));
  const effectivePanelWidth = Math.min(Math.max(panelWidth, activePanelMin), activePanelMax);

  const sidebarWidthMV = useMotionValue(300);
  const sidebarOverflow = useMotionValue(0);
  // ponytail: nur Schrumpf-Gummiband (negativ) in die Layout-Breite — Wachs-Überdehnung über das
  // Max war die einzige Quelle für Überlappung/Abstand-Fressen. Pill-Feedback bleibt erhalten.
  const sidebarDisplayWidth = useTransform(() => sidebarWidthMV.get() + Math.min(sidebarOverflow.get(), 0));

  const panelWidthMV = useMotionValue(250);
  const panelOverflow = useMotionValue(0);
  const panelDisplayWidth = useTransform(() => panelWidthMV.get() + Math.min(panelOverflow.get(), 0));
  // Studio-Vorschaukarten: Form (hochkant <-> quer) folgt der echten Panel-Breite.
  // 0 = schmalste Breite -> hochkant, 1 = breiteste -> quer. Dadurch morpht die Form
  // gleichmäßig über die ganze Ziehstrecke, ohne Plateau oder plötzliches Einsetzen.
  const sacShapeT = useTransform(
    panelDisplayWidth,
    [activePanelMin, Math.max(activePanelMin + 1, activePanelMax)],
    [0, 1],
    { clamp: true }
  );
  const markierteIds = useMemo(() => new Set(stellen.filter(s => s.gepinnt).map(s => s.id)), [stellen]);

  // Beim Reingehen merken wir die normale Breite, damit beim Rausgehen exakt
  // dorthin zurückanimiert wird (statt mit Bonus-Arithmetik zu raten).
  const prevPanelWidthRef = useRef(null);
  // Bearbeitungsmodus-Umschalter: links zu Rail, rechte Leiste wächst beim Reingehen
  // direkt auf ihre maximale Breite und kehrt beim Rausgehen zur gemerkten zurück.
  const toggleBearbeitung = () => {
    const next = !bearbeitungsmodus;
    if (!next) {
      // Syncron vor dem Re-render committen — damit block_html in derselben
      // React-Batch wie setBearbeitungsmodus(false) landet.
      document.dispatchEvent(new CustomEvent('cv-body-commit'));
    }
    // Scroll an die Dokument-Oberkante macht der StellenEditor selbst per
    // useLayoutEffect — erst nachdem das Auf-/Zuklappen der Fit-Analyse im
    // Layout steht (sonst misst er zu früh und springt zu weit).
    setBearbeitungsmodus(next);
    let ziel;
    if (next) {
      prevPanelWidthRef.current = panelWidth;
      ziel = Math.max(
        activePanelMin,
        Math.min(
          Math.round(0.40 * (viewportW - RAIL_W - SHELL_OVERHEAD)),
          viewportW - RAIL_W - CENTER_MIN - SHELL_OVERHEAD
        )
      );
    } else {
      ziel = prevPanelWidthRef.current ?? panelWidth;
    }
    setPanelWidth(ziel);
    // Rechte Leiste gleitet auf derselben Kurve/Zeit wie die linke (CSS is-rail-anim),
    // statt schlagartig zu springen — sonst ruckelt die Mitte zwischen beiden.
    animate(panelWidthMV, ziel, { duration: 0.6, ease: [0.33, 1, 0.68, 1] });
    setRailAnim(true);
    if (railAnimTimer.current) clearTimeout(railAnimTimer.current);
    railAnimTimer.current = setTimeout(() => setRailAnim(false), 700);
  };
  // Runder Stift-Knopf, fest in der oberen rechten Ecke der Dokument-Insel —
  // gleicher Abstand nach oben und rechts. Sitzt absolut in der Insel selbst
  // und scrollt deshalb mit der Seite mit. Schaltet den Bearbeitungsmodus
  // an/aus (provisorisch).
  const bearbeitenButton = (
    <button
      type="button"
      onClick={toggleBearbeitung}
      title={bearbeitungsmodus ? 'Bearbeitung beenden' : 'Bearbeiten'}
      aria-label={bearbeitungsmodus ? 'Bearbeitung beenden' : 'Bearbeiten'}
      className={`cv-bearbeiten-stift${bearbeitungsmodus ? ' is-aktiv' : ''}`}
      style={{
        position: 'absolute',
        top: 15,
        right: 15,
        zIndex: 5,
        width: 36,
        height: 36,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 9999,
        border: 'none',
        cursor: 'pointer',
        background: bearbeitungsmodus ? 'var(--cv-amber, #FFBC00)' : 'var(--cv-teal, #004D40)',
        color: bearbeitungsmodus ? 'var(--cv-teal, #004D40)' : '#fff',
        boxShadow: '0 2px 6px rgba(0,77,64,0.18)',
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
    </button>
  );

  useEffect(() => {
    const mqMobile = window.matchMedia(`(max-width: ${MOBILE_BP - 1}px)`);
    const mqUltra  = window.matchMedia('(min-aspect-ratio: 21/9) and (min-width: 1600px)');
    const update = () => {
      setIsMobile(mqMobile.matches);
      setRegime(computeRegime(window.innerWidth, mqUltra.matches));
      setViewportW(window.innerWidth);
    };
    mqMobile.addEventListener('change', update);
    mqUltra.addEventListener('change', update);
    window.addEventListener('resize', update);
    return () => {
      mqMobile.removeEventListener('change', update);
      mqUltra.removeEventListener('change', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Clamp both sidebar widths when regime or viewport changes — also enforces CENTER_MIN
  useEffect(() => {
    if (istRail || railAnim) return; // im Rail-Modus / während der Umschalt-Animation nicht eingreifen
    const { sMin, sMax, pMin, pMax } = REGIME_BOUNDS[regime];
    const available = viewportW - SHELL_OVERHEAD - CENTER_MIN;
    const sMaxClamped = Math.max(sMin, Math.min(sMax, available - panelWidthMV.get()));
    const pMaxClamped = Math.max(pMin, Math.min(pMax, available - sidebarWidthMV.get()));
    const newSW = Math.min(Math.max(sidebarWidthMV.get(), sMin), sMaxClamped);
    const newPW = Math.min(Math.max(panelWidthMV.get(),   pMin), pMaxClamped);
    setSidebarWidth(newSW); sidebarWidthMV.set(newSW);
    setPanelWidth(newPW);   panelWidthMV.set(newPW);
  }, [regime, viewportW, istRail, railAnim]); // eslint-disable-line react-hooks/exhaustive-deps



  const refreshProfile = () => {
    setProfilLaedt(true);
    getProfile().then(r => {
      setHatProfile(r.data.length > 0);
      if (r.data.length > 0) {
        const aktiv = r.data.find(p => p.aktiv) ?? r.data[0];
        const dateiname = aktiv.dateiname;
        getProfil(dateiname).then(res => setAktivesProfile({ ...res.data, dateiname })).catch(() => {});
      } else {
        setAktivesProfile(null);
      }
    }).catch(() => {}).finally(() => setProfilLaedt(false));
  };


  useEffect(() => {
    refreshProfile();
  }, []);

  useEffect(() => {
    if (profilLaedt || view !== 'dashboard') return;
    const hatOnboardingSeed = Boolean(localStorage.getItem(ONBOARDING_PROFILE_SEED_KEY));
    const setupErzwungen = localStorage.getItem(forceProfileSetupKey(nutzer)) === '1';
    const setupFertig = localStorage.getItem(firstProfileSetupDoneKey(nutzer)) === '1';
    if (setupErzwungen || (!setupFertig && hatOnboardingSeed && !aktivesProfile)) {
      setFirstSetupOffen(true);
    }
  }, [profilLaedt, aktivesProfile, view, nutzer]);

  const onPricingModalOeffnen = () => {
    setPricingModalOffen(true);
    abrechnungAbrufen().catch(() => {});
  };

  // Server nicht erreichbar? Dann sagt die Seite das, statt eine leere Liste zu zeigen.
  const [ladeFehler, setLadeFehler] = useState(false);


  const refreshStellen = useCallback(() => {
    let cachedDaten = [];
    const cached = localStorage.getItem('stellen_cache');
    if (cached) {
      try { cachedDaten = JSON.parse(cached); } catch {}
    }
    getStellen().then((r) => {
      const CACHE_FELDER = ['analyse', 'gehaltsvorstellung', 'abschick_datum', 'notizen', 'interview_datum', 'empfaenger_ansprechperson'];
      const merged = r.data.map((s) => {
        const c = cachedDaten.find((c) => c.id === s.id);
        let result = s;
        if (c) {
          const overrides = {};
          for (const feld of CACHE_FELDER) {
            if (!s[feld] && c[feld]) overrides[feld] = c[feld];
          }
          if (Object.keys(overrides).length > 0) result = { ...s, ...overrides };
        }
        // generiert_am gesetzt aber Status noch leer → live auf 'unterlagen_angefertigt' flippen
        // (normalerweise passiert das erst im StellenEditor; hier machen wir es global)
        if (result.generiert_am && !result.bewerbungs_status) {
          if (!geflipptRef.current.has(result.id)) {
            geflipptRef.current.add(result.id);
            updateStelle(result.id, { bewerbungs_status: 'unterlagen_angefertigt' }).catch(() => {});
          }
          result = { ...result, bewerbungs_status: 'unterlagen_angefertigt' };
        }
        return result;
      });
      setStellen(merged);
      setLadeFehler(false);
      setAktiveStelle((prev) => {
        if (!prev) return prev;
        return merged.find((s) => s.id === prev.id) || prev;
      });
    // Ohne diese Meldung sah ein nicht erreichbarer Server genauso aus wie „keine
    // Bewerbungen vorhanden" — der Fehler wurde still verschluckt und wirkte wie Datenverlust.
    }).catch(() => setLadeFehler(true));
  }, []);

  useEffect(() => {
    refreshStellen();
  }, [refreshStellen]);

  // Wenn ein Job fertig wird (auch wenn Editor nicht geöffnet), Stellen neu laden
  useEffect(() => {
    return onAnyJobDone(() => refreshStellen());
  }, [onAnyJobDone, refreshStellen]);

  useEffect(() => {
    const onFocus = () => refreshStellen();
    const onVisible = () => {
      if (!document.hidden) refreshStellen();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshStellen]);

  useEffect(() => {
    if (stellen.length > 0) {
      const ohneEphemer = stellen.map(({ _runningJobId, _runningStatus, ...s }) => s);
      localStorage.setItem('stellen_cache', JSON.stringify(ohneEphemer));
    }
  }, [stellen]);

  // Sync aktiveStelle from URL param when stellen are available
  useEffect(() => {
    if (!stelleIdParam || stellen.length === 0) return;
    const s = stellen.find((s) => String(s.id) === stelleIdParam);
    if (s) setAktiveStelle(s);
  }, [stelleIdParam, stellen]);

  const [pendingAutoAnalyse, setPendingAutoAnalyse] = useState(false);
  const [pendingMappeOffen, setPendingMappeOffen] = useState(false);
  // Wird erst wahr, wenn die rechte Leiste fertig aufgefahren ist -> dann klappt die Mappe
  // auf. Keine feste Wartezeit; ist die Leiste schon offen, wird es sofort wahr.
  const [mappeAuf, setMappeAuf] = useState(false);

  const onAuswahl = (stelle, opts = {}) => {
    setAktiveStelle(stelle);
    if (opts.autoAnalyse) setPendingAutoAnalyse(true);
    setPendingMappeOffen(!!opts.mappeOffen);
    setMappeAuf(false); // jede neue Auswahl startet zu; nur der Abschicken-Pfad öffnet wieder (via Effekt)
    navigate('/stelle/' + stelle.id);
    // Lebenslauf-Entwürfe landen immer direkt im Bearbeitungsmodus, nicht erst
    // nach manuellem Stift-Klick — hier gibt es ja nichts anderes zu tun.
    if (stelle.quelle === 'lebenslauf' && !bearbeitungsmodus) toggleBearbeitung();
  };

  // Gestaffelter "Bewerbung abschicken"-Effekt: Sobald die Bewerbung im Editor offen ist,
  // wächst die rechte Leiste langsam auf ihre Maximalbreite. Erst wenn diese Animation
  // fertig ist, geht die Mappe auf (mappeAuf) — ohne feste Wartezeit. Ist die Leiste schon
  // offen, entfällt die Bewegung und die Mappe startet sofort.
  useEffect(() => {
    if (view !== 'editor' || !pendingMappeOffen) return;
    const ziel = activePanelMax;
    if (Math.abs(panelWidthMV.get() - ziel) < 2) { setMappeAuf(true); return; } // schon offen -> keine Totzeit
    setPanelWidth(ziel);
    // Bewusst langsam (nur dieser Papierflieger-Pfad), damit man das Aufgehen schön sieht.
    const controls = animate(panelWidthMV, ziel, { duration: 1.4, ease: [0.33, 1, 0.68, 1] });
    setRailAnim(true); // sperrt das Breiten-Clamping während der Animation
    if (railAnimTimer.current) clearTimeout(railAnimTimer.current);
    railAnimTimer.current = setTimeout(() => setRailAnim(false), 1500);
    controls.then(() => setMappeAuf(true)).catch(() => {}); // Mappe erst wenn Leiste fertig ist
    return () => controls.stop();
  }, [view, pendingMappeOffen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ponytail: lazy-load default templates once, cache in ref
  const vorlagenRef = useRef(null);
  const ladeVorlagenDefaults = async () => {
    if (vorlagenRef.current) return vorlagenRef.current;
    const [cv, an] = await Promise.all([getCVVorlagen(), getAnschreibenVorlagen()]);
    vorlagenRef.current = {
      cvDef: (cv.data.find(v => v.dateiname === 'cv_standard') || cv.data[0])?.dateiname,
      anDef: an.data[0]?.dateiname,
    };
    return vorlagenRef.current;
  };

  const onGenerierenDirekt = async (stelle) => {
    if (!hatProfile) { setKeinProfilWarnung(true); return; }
    // no source yet → open editor so user can paste the job text
    if (!stelle.text && !stelle.url) { onAuswahl(stelle, { autoAnalyse: true }); return; }
    // analyse already done → just navigate, no need for a new run
    if (stelle.analyse) { onAuswahl(stelle); return; }
    // already running? ignore double-clicks
    if (Object.values(jobs).some(j => j.stelleId === stelle.id && j.status !== 'fertig' && j.status !== 'fehler')) return;
    try {
      const { cvDef, anDef } = await ladeVorlagenDefaults();
      const res = await starteAnalyse({
        profil_name: stelle.profil_name || aktivesProfile?.dateiname,
        stellen_text: stelle.text || undefined,
        stellen_url: stelle.url || undefined,
        stelle_id: stelle.id,
        cv_vorlage: cvDef,
        anschreiben_vorlage: anDef,
        tonlage: tonlage || 'klassisch',
      });
      const id = res.data.job_id;
      capture('job_generation_started', { source: stelle.url ? 'job_url' : 'manual_input' });
      registerJob(id, stelle.id, 'komplett');
      onGeaendert({ ...stelle, _runningJobId: id, _runningStatus: 'analyse' }, 'update');
    } catch (e) {
      console.error('[Studio] Direktstart fehlgeschlagen', e);
    }
  };

  const [neueStelleLaedt, setNeueStelleLaedt] = useState(false);

  const onNeueStelle = async () => {
    if (neueStelleLaedt) return;
    if (!hatProfile) {
      setKeinProfilWarnung(true);
      return;
    }
    setNeueStelleLaedt(true);
    try {
      // Offene Stellen (ohne generierte Dokumente) automatisch löschen
      const offeneStellen = stellen.filter(s => istEntwurf(s) && !s.url && !s.text);
      for (const offene of offeneStellen) {
        try {
          await deleteStelle(offene.id);
          setStellen(prev => prev.filter(s => s.id !== offene.id));
        } catch (e) {
          console.error('[Studio] Offene Stelle konnte nicht gelöscht werden:', e);
        }
      }

      const res = await createStelle({ name: 'Neue Stellenanzeige' });
      onGeaendert(res.data, 'neu');
    } catch (e) {
      console.error('Neue Stelle konnte nicht erstellt werden', e);
    } finally {
      setNeueStelleLaedt(false);
    }
  };

  const onMarkierenToggle = (id) => {
    const gepinnt = !stellen.find(s => s.id === id)?.gepinnt;
    setStellen(prev => prev.map(s => s.id === id ? { ...s, gepinnt } : s));
    updateStelle(id, { gepinnt }).catch(() => {
      setStellen(prev => prev.map(s => s.id === id ? { ...s, gepinnt: !gepinnt } : s));
    });
  };

  const onGeaendert = (stelle, aktion) => {
    if (aktion === 'neu') {
      setStellen((prev) => [...prev, stelle]);
      setAktiveStelle(stelle);
      navigate('/stelle/' + stelle.id);
    } else if (aktion === 'update') {
      setStellen((prev) => prev.map((s) => (s.id === stelle.id ? stelle : s)));
      if (aktiveStelle?.id === stelle.id) setAktiveStelle(stelle);
    } else if (aktion === 'loeschen') {
      setStellen((prev) => prev.filter((s) => s.id !== stelle.id));
      if (aktiveStelle?.id === stelle.id) {
        setAktiveStelle(null);
        navigate('/dashboard');
      }
    } else if (aktion === 'papierkorb') {
      setStellen((prev) => prev.map((s) => (s.id === stelle.id ? stelle : s)));
      if (aktiveStelle?.id === stelle.id) {
        setAktiveStelle(null);
        navigate('/dashboard');
      }
    }
  };

  if (isMobile) {
    if (view === 'kauf_erfolg') {
      return <KaufErfolgView onWeiter={() => navigate('/dashboard')} />;
    }
    if (view === 'kauf_abbruch') {
      return <KaufAbbruchView onNochmal={onPricingModalOeffnen} onZurueck={() => navigate('/dashboard')} kauftGrade={kauftGrade} />;
    }
    return (
      <>
        <MobileStudio
          nutzer={nutzer}
          stellen={stellen}
          setStellen={setStellen}
          aktiveStelle={aktiveStelle}
          onAuswahl={onAuswahl}
          onGeaendert={onGeaendert}
          onProfilOeffnen={() => { setProfilModalDateiname(null); setProfilModalInitialAnsicht(null); setProfilModalOffen(true); }}
          hatProfile={hatProfile}
          profilLaedt={profilLaedt}
          aktivesProfile={aktivesProfile}
          markierteIds={markierteIds}
          onMarkierenToggle={onMarkierenToggle}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          onKeinKontingent={onPricingModalOeffnen}
          rolle={rolle}
          onBasisBearbeiten={() => {
            if (!aktivesProfile) {
              setFirstSetupOffen(true);
            } else {
              setProfilModalDateiname(aktivesProfile.dateiname);
              setProfilModalInitialAnsicht(null);
              setProfilModalOffen(true);
            }
          }}
          onAbmelden={() => {
            const userKeys = [
              'stellen_cache', 'cvflow_cal_events', 'cvflow_gcal_synced_ids',
              'calendar_eintraege', 'last_profile', 'onboarding_done',
              'cvflow_onboarding_profile_seed', 'cvflow_cv_import_pending',
            ];
            userKeys.forEach(k => localStorage.removeItem(k));
            signOut(auth);
          }}
        />
        {profilModalOffen && (
          <ProfilModal
            nutzer={nutzer}
            initialDateiname={profilModalDateiname}
            initialAnsicht={profilModalInitialAnsicht}
            initialSektion={profilModalInitialSektion}
            initialSektionWert={profilModalInitialSektionWert}
            onSchliessen={() => {
              profilGeaendertRef.current = false;
              setProfilModalOffen(false); setProfilModalDateiname(null); setProfilModalInitialAnsicht(null); setProfilModalInitialSektion(null); setProfilModalInitialSektionWert(null);
              refreshProfile();
            }}
            onProfilGespeichert={(geaendertesProfil) => {
              refreshProfile();
              profilGeaendertRef.current = true;
              const stelleId = aktiveStelle?.id;
              const profilName = geaendertesProfil || aktivesProfile?.dateiname;
              if (!stelleId || !profilName || !aktiveStelle?.analyse_struktur) return;
              // Bewertung gehört ihrem Profil — fremdes Profil löst keinen Neu-Check aus
              if (aktiveStelle?.profil_name && aktiveStelle.profil_name !== profilName) return;
              const ergaenzterBegriff = profilModalInitialSektionWert;
              if (ergaenzterBegriff) {
                // ponytail: "Im Profil ergänzen"-Flow — optimistisch sofort, kein LLM (Option A)
                setAktiveStelle(prev => {
                  if (!prev?.analyse_struktur?.relevante_keywords) return prev;
                  return { ...prev, analyse_struktur: { ...prev.analyse_struktur, relevante_keywords: prev.analyse_struktur.relevante_keywords.map(kw => (kw.name || kw.begriff || '').toLowerCase() === ergaenzterBegriff.toLowerCase() ? { ...kw, match_typ: 'exact' } : kw) } };
                });
                setStellen(prev => prev.map(s => {
                  if (s.id !== stelleId || !s.analyse_struktur?.relevante_keywords) return s;
                  return { ...s, analyse_struktur: { ...s.analyse_struktur, relevante_keywords: s.analyse_struktur.relevante_keywords.map(kw => (kw.name || kw.begriff || '').toLowerCase() === ergaenzterBegriff.toLowerCase() ? { ...kw, match_typ: 'exact' } : kw) } };
                }));
              } else {
                // Manuelle Profiländerung: string-match sofort + LLM im Hintergrund
                getProfil(profilName).then(res => {
                  const alleKenntnisse = (res.data?.sektionen || []).filter(s => s.typ === 'kenntnisse').flatMap(s => s.kenntnisse || []).map(k => k.toLowerCase());
                  const istDrin = (kw) => {
                    const b = (kw.name || kw.begriff || '').toLowerCase();
                    const vs = (kw.varianten || []).map(v => v.toLowerCase());
                    return [b, ...vs].some(t => alleKenntnisse.some(k => k.includes(t) || t.includes(k)));
                  };
                  const applyStringMatch = (keywords) => keywords.map(kw => {
                    const drin = istDrin(kw);
                    if (kw.match_typ === 'fehlt' && drin) return { ...kw, match_typ: 'exact' };
                    if (kw.match_typ !== 'fehlt' && !drin) return { ...kw, match_typ: 'fehlt' };
                    return kw;
                  });
                  setAktiveStelle(prev => {
                    if (!prev?.analyse_struktur?.relevante_keywords) return prev;
                    return { ...prev, analyse_struktur: { ...prev.analyse_struktur, relevante_keywords: applyStringMatch(prev.analyse_struktur.relevante_keywords) } };
                  });
                  setStellen(prev => prev.map(s => {
                    if (s.id !== stelleId || !s.analyse_struktur?.relevante_keywords) return s;
                    return { ...s, analyse_struktur: { ...s.analyse_struktur, relevante_keywords: applyStringMatch(s.analyse_struktur.relevante_keywords) } };
                  }));
                }).catch(() => {});
                rematchKeywords(stelleId, profilName).then(res => {
                  if (!res?.data?.analyse_struktur) return;
                  setAktiveStelle(prev => prev?.id === stelleId ? { ...prev, analyse_struktur: res.data.analyse_struktur } : prev);
                  setStellen(prev => prev.map(s => s.id === stelleId ? { ...s, analyse_struktur: res.data.analyse_struktur } : s));
                }).catch(() => {});
              }
            }}
          />
        )}
        {firstSetupOffen && (
          <FirstProfileSetupModal
            nutzer={nutzer}
            onFertig={() => {
              localStorage.removeItem(forceProfileSetupKey(nutzer));
              setFirstSetupOffen(false);
              refreshProfile();
            }}
          />
        )}
        <CvImportOverlay />
        {pricingModalOffen && (
          <PricingModal
            offen={pricingModalOffen}
            onSchliessen={() => setPricingModalOffen(false)}
            onKaufen={(preisId, ts) => { setPricingModalOffen(false); kaufen(preisId, ts); }}
            kauftGrade={kauftGrade}
          />
        )}
      </>
    );
  }

  return (
    <div className="studio">
      {ladeFehler && (
        <div className="studio-ladefehler" role="alert">
          Der Server ist gerade nicht erreichbar — deine Bewerbungen sind sicher gespeichert
          und werden nur nicht angezeigt.
          <button type="button" onClick={refreshStellen}>Erneut versuchen</button>
        </div>
      )}
      <DotField
        className="app-dot-bg dot-field-container"
        dotRadius={1.3}
        dotSpacing={18}
        rowOffset={0.5}
        cursorRadius={0}
        glowRadius={0}
        glowColor="transparent"
        gradientFrom="rgba(120, 85, 45, 0.32)"
        gradientTo="rgba(100, 70, 35, 0.24)"
      />
      {/* Linke Sidebar – volle Höhe */}
      <motion.div
        ref={sidebarRef}
        className={`studio-sidebar${istRail ? ' is-rail' : ''}${railAnim ? ' is-rail-anim' : ''}`}
        style={{ width: istRail ? RAIL_W : sidebarDisplayWidth }}
        onClickCapture={istRail ? (e) => { e.preventDefault(); e.stopPropagation(); toggleBearbeitung(); } : undefined}
      >
        <div className="sidebar-brand">
          <Link to="/" className="sidebar-logo-link">
            <img src="/logo.png" alt="CVFlow" className="sidebar-logo-img" />
          </Link>
          <span className="sidebar-brand-name">NAVIS</span>
        </div>

        <nav className="sidebar-nav">
          <AnimateIcon animateOnHover asChild>
            <button
              className={`sidebar-nav-link ${view === 'dashboard' ? 'aktiv' : ''}`}
              onClick={() => navigate('/dashboard')}
            >
              <span className="sidebar-nav-icon"><LayoutDashboard size={19} /></span>
              <span className="sidebar-nav-label">Dashboard</span>
            </button>
          </AnimateIcon>
          <AnimateIcon animateOnHover asChild>
            <button
              className={`sidebar-nav-link ${view === 'suche' ? 'aktiv' : ''}`}
              onClick={() => navigate('/suche')}
            >
              <span className="sidebar-nav-icon"><SearchAnimated size={19} /></span>
              <span className="sidebar-nav-label">Bewerbungen Suchen</span>
            </button>
          </AnimateIcon>
          <AnimateIcon animateOnHover asChild>
            <button
              className={`sidebar-nav-link ${view === 'analytics' ? 'aktiv' : ''}`}
              onClick={() => navigate('/analytics')}
            >
              <span className="sidebar-nav-icon"><ChartColumn size={19} /></span>
              <span className="sidebar-nav-label">Analytics</span>
            </button>
          </AnimateIcon>
          <AnimateIcon animateOnHover asChild>
            <button
              className={`sidebar-nav-link ${view === 'dokumente' ? 'aktiv' : ''}`}
              onClick={() => navigate('/meine-dokumente')}
            >
              <span className="sidebar-nav-icon"><ClipboardCheck size={19} /></span>
              <span className="sidebar-nav-label">Meine Dokumente</span>
            </button>
          </AnimateIcon>
          {rolle === 'admin' && (
            <button
              className={`sidebar-nav-link ${view === 'admin' ? 'aktiv' : ''}`}
              style={{ marginTop: 8, borderTop: '1px solid var(--border-sidebar)', paddingTop: 16, paddingBottom: 6 }}
              onClick={() => navigate('/admin')}
            >
              <span className="sidebar-nav-icon"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></span>
              <span className="sidebar-nav-label">Admin</span>
            </button>
          )}
        </nav>

        <StellenListe
          collapsed={istRail}
          stellen={cvflowStellen}
          aktivId={aktiveStelle?.id}
          onAuswahl={onAuswahl}
          onGeaendert={onGeaendert}
          markierteIds={markierteIds}
          onMarkierenToggle={onMarkierenToggle}
          onNeueStelle={onNeueStelle}
          neueLaedt={neueStelleLaedt}
          hasLebenslauf={hatLebenslaufInhalt(aktivesProfile)}
          profile={aktivesProfile}
          onBasisBearbeiten={() => {
            if (!aktivesProfile) {
              setFirstSetupOffen(true);
            } else {
              setProfilModalDateiname(aktivesProfile.dateiname);
              setProfilModalInitialAnsicht(null);
              setProfilModalOffen(true);
            }
          }}
          onLebenslaufHinzufuegen={() => setLebenslaufUploadOffen(true)}
        />
      </motion.div>

      {istRail ? null /* Rail liegt selbst im Fluss (60px), Resize-Griff entfällt */ : (
        <ElasticResizeHandle
          value={sidebarWidth}
          min={activeSidebarMin}
          max={activeSidebarMax}
          direction="right"
          onDrag={(v) => { setSidebarWidth(v); sidebarWidthMV.set(v); }}
          externalOverflow={sidebarOverflow}
        />
      )}

      {/* Mittlere Spalte: Topbar + Inhalt */}
      <div className="studio-center">
        <div className="studio-topbar">
        </div>

        <div className="studio-main">
          {view === 'dashboard' && (
            <Dashboard
              stellen={cvflowStellen}
              nutzer={nutzer}
              profile={aktivesProfile}
              hasLebenslauf={hatLebenslaufInhalt(aktivesProfile)}
              onNeueStelle={onNeueStelle}
              onStelleAuswaehlen={onAuswahl}
              onStelleLoeschen={(s) => onGeaendert(s, 'loeschen')}
              onStelleGeaendert={onGeaendert}
              markierteIds={markierteIds}
              onMarkierenToggle={onMarkierenToggle}
              onGenerierenDirekt={onGenerierenDirekt}
              onBasisBearbeiten={() => {
                if (!aktivesProfile) {
                  setFirstSetupOffen(true);
                } else {
                  setProfilModalDateiname(aktivesProfile.dateiname);
                  setProfilModalInitialAnsicht(null);
                  setProfilModalOffen(true);
                }
              }}
              onLebenslaufHinzufuegen={() => setLebenslaufUploadOffen(true)}
            />
          )}
          {view === 'analytics' && (
            <Analytics
              stellen={stellen}
              setStellen={setStellen}
              markierteIds={markierteIds}
              onMarkierenToggle={onMarkierenToggle}
              onGeaendert={onGeaendert}
            />
          )}
          {view === 'suche' && (
            <SucheView stellen={cvflowStellen} onStelleAuswaehlen={onAuswahl} />
          )}
          {view === 'profileinstellungen' && (
            <ProfilEinstellungen nutzer={nutzer} />
          )}
          {view === 'dokumente' && (
            <MeineDokumente
              nutzer={nutzer}
              aktivesProfile={aktivesProfile}
              stellen={cvflowStellen}
              lebenslaufEntwuerfe={lebenslaufEntwuerfe}
              onStelleOeffnen={onAuswahl}
            />
          )}
          {view === 'admin' && rolle === 'admin' && (
            <AdminPanel nutzer={nutzer} />
          )}
          {view === 'kauf_erfolg' && (
            <KaufErfolgView onWeiter={() => navigate('/dashboard')} />
          )}
          {view === 'kauf_abbruch' && (
            <KaufAbbruchView onNochmal={() => onPricingModalOeffnen()} onZurueck={() => navigate('/dashboard')} kauftGrade={kauftGrade} />
          )}
          {view === 'editor' && (
            <StellenEditor
              stelle={aktiveStelle}
              tonlage={tonlage}
              onStelleGeaendert={(s, a) => onGeaendert(s, a)}
              panelWidth={effectivePanelWidth}
              panelWidthMV={panelDisplayWidth}
              onKeinKontingent={onPricingModalOeffnen}
              autoAnalyse={pendingAutoAnalyse}
              onAutoAnalyseDone={() => setPendingAutoAnalyse(false)}
              defaultMappeOffen={mappeAuf}
              bearbeitungsmodus={bearbeitungsmodus}
              bearbeitenButton={bearbeitenButton}
              onKenntnisseErgaenzen={(begriff) => {
                setProfilModalDateiname(aktivesProfile?.dateiname ?? null);
                setProfilModalInitialAnsicht('bearbeiten');
                setProfilModalInitialSektion('kenntnisse');
                setProfilModalInitialSektionWert(begriff ?? null);
                setProfilModalOffen(true);
              }}
            />
          )}
        </div>
      </div>

      {(view === 'editor' || view === 'dashboard') && (
        <ElasticResizeHandle
          value={effectivePanelWidth}
          min={activePanelMin}
          max={activePanelMax}
          direction="left"
          onDrag={(v) => { setPanelWidth(v); panelWidthMV.set(v); }}
          externalOverflow={panelOverflow}
        />
      )}

      {view === 'editor' && (
        <motion.div className="editor-right-column" style={{
          width: panelDisplayWidth,
          flexShrink: 0,
          height: 'calc(100vh - 24px)',
          minHeight: 0,
        }}>
          <PanelActions
            onFeedbackOeffnen={() => { setFeedbackModalOffen(true); setFeedbackGesendet(false); setFeedbackBetreff(''); setFeedbackNachricht(''); setFeedbackBilder([]); setFeedbackFehler(''); }}
            darkMode={darkMode}
            onToggleDarkMode={toggleDarkMode}
            onProfilOeffnen={() => setProfilModalOffen(true)}
          />
          <div id="editor-panel-slot" style={{ flex: 1, minHeight: 0, position: 'relative' }} />
        </motion.div>
      )}

      {view === 'dashboard' && (
        <motion.div style={{ width: panelDisplayWidth, flexShrink: 0, overflow: 'visible', position: 'relative', zIndex: 0, '--sac-t': sacShapeT }}>
          <ToolPanel
            tonlage={tonlage}
            onTonlageChange={setTonlage}
            nutzer={nutzer}
            onProfilOeffnen={() => setProfilModalOffen(true)}
            profil={aktivesProfile}
            onProfilRefresh={refreshProfile}
            width="100%"
            onFeedbackOeffnen={() => { setFeedbackModalOffen(true); setFeedbackGesendet(false); setFeedbackBetreff(''); setFeedbackNachricht(''); setFeedbackBilder([]); setFeedbackFehler(''); }}
            darkMode={darkMode}
            onToggleDarkMode={toggleDarkMode}
          />
        </motion.div>
      )}

      {profilModalOffen && (
        <ProfilModal
          nutzer={nutzer}
          initialDateiname={profilModalDateiname}
          initialAnsicht={profilModalInitialAnsicht}
          initialSektion={profilModalInitialSektion}
          initialSektionWert={profilModalInitialSektionWert}
          onSchliessen={() => {
            profilGeaendertRef.current = false;
            setProfilModalOffen(false);
            setProfilModalDateiname(null);
            setProfilModalInitialAnsicht(null);
            setProfilModalInitialSektion(null);
            setProfilModalInitialSektionWert(null);
            refreshProfile();
          }}
          onProfilGespeichert={(geaendertesProfil) => {
            refreshProfile();
            profilGeaendertRef.current = true;
            const stelleId = aktiveStelle?.id;
            const profilName = geaendertesProfil || aktivesProfile?.dateiname;
            if (!stelleId || !profilName || !aktiveStelle?.analyse_struktur) return;
            // Bewertung gehört ihrem Profil — fremdes Profil löst keinen Neu-Check aus
            if (aktiveStelle?.profil_name && aktiveStelle.profil_name !== profilName) return;
            const ergaenzterBegriff = profilModalInitialSektionWert;
            if (ergaenzterBegriff) {
              // ponytail: "Im Profil ergänzen"-Flow — optimistisch sofort, kein LLM (Option A)
              setAktiveStelle(prev => {
                if (!prev?.analyse_struktur?.relevante_keywords) return prev;
                return { ...prev, analyse_struktur: { ...prev.analyse_struktur, relevante_keywords: prev.analyse_struktur.relevante_keywords.map(kw => (kw.name || kw.begriff || '').toLowerCase() === ergaenzterBegriff.toLowerCase() ? { ...kw, match_typ: 'exact' } : kw) } };
              });
              setStellen(prev => prev.map(s => {
                if (s.id !== stelleId || !s.analyse_struktur?.relevante_keywords) return s;
                return { ...s, analyse_struktur: { ...s.analyse_struktur, relevante_keywords: s.analyse_struktur.relevante_keywords.map(kw => (kw.name || kw.begriff || '').toLowerCase() === ergaenzterBegriff.toLowerCase() ? { ...kw, match_typ: 'exact' } : kw) } };
              }));
            } else {
              // Manuelle Profiländerung: string-match sofort + LLM im Hintergrund
              getProfil(profilName).then(res => {
                const alleKenntnisse = (res.data?.sektionen || []).filter(s => s.typ === 'kenntnisse').flatMap(s => s.kenntnisse || []).map(k => k.toLowerCase());
                const istDrin = (kw) => {
                  const b = (kw.name || kw.begriff || '').toLowerCase();
                  const vs = (kw.varianten || []).map(v => v.toLowerCase());
                  return [b, ...vs].some(t => alleKenntnisse.some(k => k.includes(t) || t.includes(k)));
                };
                const applyStringMatch = (keywords) => keywords.map(kw => {
                  const drin = istDrin(kw);
                  if (kw.match_typ === 'fehlt' && drin) return { ...kw, match_typ: 'exact' };
                  if (kw.match_typ !== 'fehlt' && !drin) return { ...kw, match_typ: 'fehlt' };
                  return kw;
                });
                setAktiveStelle(prev => {
                  if (!prev?.analyse_struktur?.relevante_keywords) return prev;
                  return { ...prev, analyse_struktur: { ...prev.analyse_struktur, relevante_keywords: applyStringMatch(prev.analyse_struktur.relevante_keywords) } };
                });
                setStellen(prev => prev.map(s => {
                  if (s.id !== stelleId || !s.analyse_struktur?.relevante_keywords) return s;
                  return { ...s, analyse_struktur: { ...s.analyse_struktur, relevante_keywords: applyStringMatch(s.analyse_struktur.relevante_keywords) } };
                }));
              }).catch(() => {});
              rematchKeywords(stelleId, profilName).then(res => {
                if (!res?.data?.analyse_struktur) return;
                setAktiveStelle(prev => prev?.id === stelleId ? { ...prev, analyse_struktur: res.data.analyse_struktur } : prev);
                setStellen(prev => prev.map(s => s.id === stelleId ? { ...s, analyse_struktur: res.data.analyse_struktur } : s));
              }).catch(() => {});
            }
          }}
        />
      )}

      {firstSetupOffen && (
        <FirstProfileSetupModal
          nutzer={nutzer}
          onFertig={() => {
            localStorage.removeItem(forceProfileSetupKey(nutzer));
            setFirstSetupOffen(false);
            refreshProfile();
          }}
        />
      )}

      <CvImportOverlay />
      {!firstSetupOffen && view === 'dashboard' && <DashboardTutorial />}
      {view === 'analytics' && <AnalyticsTutorial />}
      {view === 'editor' && <StellenEditorTutorial />}

      {lebenslaufUploadOffen && aktivesProfile && (
        <FirstProfileSetupModal
          nutzer={nutzer}
          initialStep={1}
          initialDateiname={aktivesProfile.dateiname}
          hasLebenslauf={hatLebenslaufInhalt(aktivesProfile)}
          onFertig={() => {
            setLebenslaufUploadOffen(false);
            refreshProfile();
          }}
        />
      )}

      {pricingModalOffen && (
        <PricingModal
          offen={pricingModalOffen}
          onSchliessen={() => setPricingModalOffen(false)}
          onKaufen={(preisId, ts) => { setPricingModalOffen(false); kaufen(preisId, ts); }}
          kauftGrade={kauftGrade}
        />
      )}

      {keinProfilWarnung && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 900,
        }} onClick={() => setKeinProfilWarnung(false)}>
          <div style={{
            background: 'var(--bg-elevated)',
            borderRadius: 20,
            padding: '40px 36px',
            maxWidth: 400,
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>👤</div>
            <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 20, marginBottom: 10, color: 'var(--txt-primary)' }}>
              Noch kein Profil vorhanden
            </h2>
            <p style={{ fontSize: 14, color: 'var(--txt-muted)', lineHeight: 1.6, marginBottom: 28 }}>
              Damit dein Anschreiben personalisiert werden kann, musst du zuerst ein Profil mit deinen Daten anlegen.
            </p>
            <button
              style={{
                width: '100%', padding: '13px', marginBottom: 10,
                background: 'var(--clr-primary)', color: 'var(--clr-secondary)',
                border: 'none', borderRadius: 30, fontSize: 15, fontWeight: 700,
                cursor: 'pointer', boxShadow: 'var(--shadow-amber-sm)',
              }}
              onClick={() => {
                setKeinProfilWarnung(false);
                setProfilModalOffen(true);
              }}
            >
              Profil erstellen
            </button>
            <button
              style={{
                width: '100%', padding: '11px',
                background: 'none', border: '1.5px solid var(--border-subtle)',
                borderRadius: 30, fontSize: 14, color: 'var(--txt-muted)', cursor: 'pointer',
              }}
              onClick={() => setKeinProfilWarnung(false)}
            >
              Später
            </button>
          </div>
        </div>
      )}
      {feedbackModalOffen && (
        <div className="feedback-overlay" onClick={() => setFeedbackModalOffen(false)}>
          <div className="feedback-modal" onClick={e => e.stopPropagation()}>
            <button className="feedback-close" onClick={() => setFeedbackModalOffen(false)}>✕</button>
            {feedbackGesendet ? (
              <div className="feedback-success">
                <div className="feedback-success-icon">💡</div>
                <h3>Danke für dein Feedback!</h3>
                <p>Wir melden uns so schnell wie möglich bei dir.</p>
                <button className="feedback-btn-primary" onClick={() => setFeedbackModalOffen(false)}>Schließen</button>
              </div>
            ) : (
              <>
                <div className="feedback-header">
                  <IdeaIcon size={22} color="var(--accent)" />
                  <h3>Feature wünschen / Bug melden</h3>
                </div>
                <p className="feedback-sub">Teile uns mit, was du dir wünschst oder was nicht funktioniert.</p>
                <label className="feedback-label">Betreff</label>
                <input
                  className="feedback-input"
                  placeholder="z. B. Export als PDF funktioniert nicht"
                  value={feedbackBetreff}
                  onChange={e => setFeedbackBetreff(e.target.value)}
                  maxLength={120}
                />
                <label className="feedback-label">Nachricht</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Beschreibe den Bug oder dein Wunsch-Feature so genau wie möglich…"
                  value={feedbackNachricht}
                  onChange={e => setFeedbackNachricht(e.target.value)}
                  rows={4}
                  maxLength={2000}
                />
                <label className="feedback-label">Screenshots <span className="feedback-label-hint">(optional, max. 3 × 5 MB)</span></label>
                {feedbackBilder.length > 0 && (
                  <div className="feedback-thumbs">
                    {feedbackBilder.map((b, i) => (
                      <div key={i} className="feedback-thumb-wrap">
                        <img src={b.dataUrl} alt={b.dateiname} className="feedback-thumb" />
                        <button className="feedback-thumb-remove" onClick={() => setFeedbackBilder(prev => prev.filter((_, j) => j !== i))}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {feedbackBilder.length < 3 && (
                  <label className="feedback-btn-ghost feedback-file-label">
                    <input
                      className="feedback-file-input"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={e => {
                        setFeedbackFehler('');
                        const files = Array.from(e.target.files || []);
                        if (feedbackBilder.length + files.length > 3) {
                          setFeedbackFehler('Maximal 3 Bilder erlaubt.');
                          e.target.value = '';
                          return;
                        }
                        const tooBig = files.find(f => f.size > 5 * 1024 * 1024);
                        if (tooBig) {
                          setFeedbackFehler(`"${tooBig.name}" ist größer als 5 MB.`);
                          e.target.value = '';
                          return;
                        }
                        Promise.all(files.map(f => new Promise((res, rej) => {
                          const r = new FileReader();
                          r.onload = () => res({ dateiname: f.name, dataUrl: r.result });
                          r.onerror = rej;
                          r.readAsDataURL(f);
                        }))).then(neu => setFeedbackBilder(prev => [...prev, ...neu]));
                        e.target.value = '';
                      }}
                    />
                    + Bild hinzufügen
                  </label>
                )}
                {feedbackFehler && <p className="feedback-error">{feedbackFehler}</p>}
                <button
                  className="feedback-btn-primary"
                  disabled={feedbackLaedt || !feedbackBetreff.trim() || !feedbackNachricht.trim()}
                  onClick={async () => {
                    setFeedbackLaedt(true);
                    setFeedbackFehler('');
                    try {
                      const anhaenge = feedbackBilder.map(b => ({
                        dateiname: b.dateiname,
                        inhalt_base64: b.dataUrl.split(',')[1],
                      }));
                      await feedbackSenden(feedbackBetreff.trim(), feedbackNachricht.trim(), anhaenge);
                      setFeedbackGesendet(true);
                    } catch {
                      setFeedbackFehler('Fehler beim Senden. Bitte nochmal versuchen.');
                    } finally {
                      setFeedbackLaedt(false);
                    }
                  }}
                >
                  {feedbackLaedt ? 'Wird gesendet…' : 'Absenden →'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {view === 'dashboard' && (
        <Papierkorb stellen={stellen} setStellen={setStellen} panelWidth={panelDisplayWidth} />
      )}
    </div>
  );
}

export default Studio;
